/**
 * Subscribe to manage-backend websocket payment updates for a single order.
 * Returns cleanup fn. Safe no-op when WebSocket unavailable.
 *
 * `token` is the caller's own staff/driver-portal bearer token — the /ws
 * socket now authenticates connections the same way the rest of the API
 * does, so a page with no token to present (e.g. the public customer proof
 * page) can't open one and falls back to its own polling instead.
 */
export function subscribeOrderPaymentUpdated({ orderId, orderNumber, token, onUpdate }) {
  if (typeof window === 'undefined' || typeof WebSocket === 'undefined') {
    return () => {};
  }
  const oid = String(orderId || '').trim();
  const onum = String(orderNumber || '').trim();
  const authToken = String(token || '').trim();
  if ((!oid && !onum) || !authToken) return () => {};

  let ws;
  let closed = false;
  let retryTimer;

  const connect = () => {
    if (closed) return;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    try {
      ws = new WebSocket(`${proto}//${window.location.host}/ws`, [authToken]);
    } catch {
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.type !== 'order_payment_updated') return;
        const mid = String(msg.order_id || '').trim();
        const mnum = String(msg.order_number || '').trim();
        if ((oid && mid === oid) || (onum && mnum === onum) || (!mid && !mnum)) {
          onUpdate?.(msg);
        }
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      if (!closed) retryTimer = window.setTimeout(connect, 4000);
    };
  };

  connect();

  return () => {
    closed = true;
    if (retryTimer) window.clearTimeout(retryTimer);
    try { ws?.close(); } catch { /* ignore */ }
  };
}
